import type { EventId, FigureId, SettlementId, WitnessSource } from "../world/types";

/*
 * The lives the world can honestly offer a player to be born into.
 *
 * Phase 6 plan, wave 3C — *"being born"* — over decision 3 (**B-lite**) and
 * `docs/research/player-fantasies/fantasy-01-born-into-the-world.md` §2: the
 * origins screen enumerates lives the *simulation* already lived, the player
 * picks one, and `Command::Born` binds it. Three doors, in the order the plan
 * ships them — the orphan of a ruined town, the child no chronicle recorded, and
 * the child the chronicles already wrote down.
 *
 * **This is the only module that talks to the origins surface**, which is the
 * same decision `witness/saga.ts` is under and it is here for the same reason:
 * the boundary chunk (3B) and this one are written at the same time, so
 * everything that could drift between them is squeezed into one file. What a
 * card *is* belongs to `world/wasm.ts` and is declared on `world/types.ts`; this
 * file reads that surface, and two rules live on this side of the seam:
 *
 * - **The surface is presence-checked rather than assumed.** A build whose
 *   boundary has not grown `origins()` — or has grown the read and not the door —
 *   opens no screen and says nothing false, which is the answer this client
 *   already gives to a world with no history in it at all (`?bootstrap=0`).
 * - **A card the log cannot account for is refused and counted.** Every life on
 *   the screen cites the record behind it — the sack, or the birth — and every
 *   claim under it cites the record that seated the parent. A card that cites
 *   nothing, or that could not be handed back to the door it came from, is not a
 *   card: it is dropped and the count is asserted on
 *   (`dev/selfcheck.ts`'s `checkOrigins`).
 *
 * # What this file composes, and what it never composes
 *
 * Unlike the saga, where `sim-core` writes the sentence and the client turns the
 * page, **the card's line is assembled here** — and the difference is worth being
 * precise about, because it looks like the rule being bent.
 *
 * A card is a *label on a menu of lives*, not a sentence about what happened. The
 * simulation composes prose about records (the Saga register, the chronicler's
 * narration, a spoken line) and this file asks for none of that: it takes the
 * proper nouns the source answered with — a town's name, a parent's name, a
 * house's name, an office's title — and puts the plainest possible connective
 * words between them. **Every name on a card comes off the source**, which is a
 * thing the self-check reads back rather than a promise this comment makes: a
 * capitalised word on a card that the source never said is a failure there.
 *
 * The other half of the same rule is the degradations below. A card whose source
 * gave no name for a parent says *"a child no chronicle recorded"* and stops —
 * it does not reach for a placeholder, and it does not say "someone". A clause
 * with nothing behind it is dropped, never filled.
 */

/** One seat a card's parents held: what the claim says, and the record that seated them. */
export interface OriginClaim {
  /** *"child of the ruler of Eldbeck"* — nouns from the source, connectives from here. */
  readonly line: string;

  /** The record behind it. Never empty: a claim that cites nothing is refused. */
  readonly cites: readonly EventId[];
}

/** One life the screen offers, and everything the door needs to bind it. */
export interface OriginChoice {
  /** Which door this is. The sim's own three (`history::origins::OriginKind`). */
  readonly kind: OriginKindName;

  /** The card's line, as a reader sees it. Assembled here; see the header. */
  readonly line: string;

  /** The seats this life's parents held, as sentences with their records. */
  readonly claims: readonly OriginClaim[];

  /** The record behind the offer — the sack, or the birth. Never empty. */
  readonly cites: readonly EventId[];

  /**
   * The numbers a {@link BirthDoor} call carries back.
   *
   * **The round trip is the whole point**: the card the screen drew hands back
   * exactly the citation the door validates, so nothing above this seam ever
   * assembles a birth selector and nothing can assemble a wrong one
   * (`history::origins::Origin::birth`).
   */
  readonly settlement: SettlementId | null;
  readonly mother: FigureId | null;
  readonly father: FigureId | null;
  readonly figure: FigureId | null;
}

/**
 * The doors, by the name `OriginKind::name` gives each of them.
 *
 * Three until wave 2D-a and four since: `heir` is the door permadeath opens
 * (decision 7), and it is **empty in every session the player is alive in** — a
 * start screen still shows three doors' worth of lives, and this one carries the
 * kin of a line the session has already lost.
 */
export type OriginKindName = "orphan" | "unrecorded" | "bound" | "heir";

/** Everything the screen may offer, and what the client would not put on it. */
export interface OriginsOffer {
  /** The lives, in the order the source listed them. Empty in a build with no export. */
  readonly cards: readonly OriginChoice[];

  /**
   * What was dropped on the way in.
   *
   * **Non-zero is a defect, not a taste** — the same standing this client gives
   * an uncited saga line (`witness/saga.ts`'s `SagaBook.refused`). A card is
   * refused when it cites nothing, when its door is one this build does not know,
   * or when it carries no way of being handed back; a claim is refused when it
   * cites nothing or when there is no honest sentence to say about it.
   */
  readonly refused: { readonly cards: number; readonly claims: number };
}

/** No lives on offer: what a build without the export reads, and a world with none. */
export const EMPTY_ORIGINS: OriginsOffer = { cards: [], refused: { cards: 0, claims: 0 } };

/**
 * `?born=0` — the birth screen, off. Default **on**: being born is how a session
 * starts once the world can offer a life.
 *
 * Spelled the way `?book=0` is (`witness/saga.ts`'s `BOOK_PARAM`) rather than as
 * an opt-*out* flag, because it is the same kind of thing: a start-flow step that
 * ships on, with a parameter for the session that declines it. One link opens the
 * phase's first minute and one opens the Phase 5 first minute, which is what
 * makes them one playtest instead of two builds.
 */
const BORN_PARAM = "born";

/**
 * The commands a birth is queued through (3B's half of the door).
 *
 * Every member optional, and that is the presence check rather than a
 * convenience: a build whose boundary has grown the *read* and not the *door* can
 * still answer `origins()`, and a screen that offered lives it could not record
 * would be the worst of the three possible half-builds. `WorldSource` satisfies
 * this structurally, so nothing is wrapped and nothing new crosses the seam —
 * the same bargain `PlayerDeeds` makes for the Phase 5 verbs
 * (`witness/witness.ts`).
 *
 * One call per door rather than one call with a tagged payload, because that is
 * the shape the selector has sim-side: an orphan cannot name a mother and a bound
 * child cannot name a town it was not born in, and three signatures make those
 * states unrepresentable at the seam as well as inside it.
 *
 * The three verbs landed on `WorldSource` in the same wave this was written, and
 * the assignment in `main.ts` is what checks that they still agree: the members
 * stay optional here because *presence* is what the screen asks about, and the
 * compiler answers the other question — whether the spellings match — at the one
 * line where a world is handed over as a door.
 */
export interface BirthDoor {
  queueBornOrphan?(name: string, settlement: SettlementId, coinNonce?: number): void;
  queueBornUnrecorded?(
    name: string,
    mother: FigureId,
    father: FigureId,
    coinNonce?: number,
  ): void;
  queueBornBound?(name: string, figure: FigureId, coinNonce?: number): void;

  /**
   * The fourth door, and a *separate* member although it carries the bound
   * door's payload.
   *
   * The two are two eligibility rules over one mechanism (`sim_core`'s
   * `history::origins`): binding takes over a stranger the screen picked out of
   * the world and is fenced accordingly — grown, unseated, young enough — and
   * this one takes over somebody the late player was kin to, at whatever age the
   * line has left. A card that came back through the wrong door would be a life
   * the *other* door's rules then refuse, which for a child heir is every time.
   */
  queueBornHeir?(name: string, figure: FigureId, coinNonce?: number): void;

  /*
   * And what the same three doors would *coin*, at one press of the button.
   *
   * **The surname ruling's read half** (owner, 2026-08-25): the family name is
   * the world's to write and what the player chooses is which of its draws to
   * live with, so the screen offers another draw and each press submits the same
   * birth with the next nonce. Seeing the draw is what makes choosing between
   * them a choice.
   *
   * Optional like the three above and presence-checked the same way
   * ({@link coinedNameOf} answers the empty string where a build has not grown
   * them), because a boundary halfway through a wave may have the doors and not
   * the previews — and a button that showed nothing would be worse than no
   * button.
   *
   * They are **reads**: nothing is queued and nothing about the world moves. What
   * they cost is a copy of the simulation, which pre-spawn is the history tier
   * and nothing else (`world/types.ts`).
   */

  coinedOrphanHouse?(name: string, settlement: SettlementId, coinNonce: number): string;
  coinedUnrecordedHouse?(
    name: string,
    mother: FigureId,
    father: FigureId,
    coinNonce: number,
  ): string;
  coinedBoundHouse?(name: string, figure: FigureId, coinNonce: number): string;
}

/**
 * The first draw of a coined family name: nobody has pressed anything.
 *
 * `sim_core::history::house`'s `FIRST_COIN`, which is the nonce every caller in
 * the simulation passes and the one a birth screen submits when the button was
 * never touched. Stated here rather than left as a literal zero for that
 * constant's own reason: a bare `0` at a call site leaves a reader guessing
 * which of them meant *the first draw* and which meant *no draw at all*.
 */
export const FIRST_COIN = 0;

/**
 * What an origins-capable boundary answers with, as a *reading* of it.
 *
 * Declared here rather than imported because `world/types.ts` belongs to the
 * boundary chunk and the two are being written at once. Everything is optional
 * and every field is read defensively, so a shape that lands slightly differently
 * costs one file: the fields below are the house idiom for a row that carries
 * both a handle and its name (`RosterEntry`, `TradePulse.talkerName`,
 * `ChronicleView.authorName`), and a boundary that answers with only the numbers
 * still draws honest cards with the clauses it cannot fill left out.
 *
 * The one thing that is *not* tolerated is a card with nothing behind it: see
 * {@link OriginsOffer.refused}.
 */
export interface OriginsSurface {
  origins?(): readonly RawOrigin[];
}

/**
 * What {@link readOrigins} needs, which is two reads and not a world.
 *
 * Named down to `settlements` and `origins` rather than taking the whole
 * {@link WitnessSource}, because that is honestly all it touches — and because it
 * is what lets a *stand-in* be a plain object rather than a copy of a class
 * (`dev/birthfake.ts`; the live source is a class, and spreading one of those
 * leaves its methods on the prototype it came from). A full source satisfies it,
 * so nothing at the call site changes when the boundary grows the export.
 */
export type OriginsReading = Pick<WitnessSource, "settlements"> & Partial<OriginsSurface>;

/** One row of {@link OriginsSurface.origins}, read for what it actually carries. */
export interface RawOrigin {
  /** The door, as `OriginKind`'s id. */
  readonly kind?: number | string;

  /** The door's own word — *orphan*, *unrecorded*, *bound*. Read if the id is absent. */
  readonly kindName?: string;

  readonly settlement?: number | null;
  readonly settlementName?: string;

  /** The record behind the offer. One id or a list of them; both are read. */
  readonly cites?: number | readonly number[];

  readonly mother?: number | null;
  readonly motherName?: string;
  readonly father?: number | null;
  readonly fatherName?: string;
  readonly figure?: number | null;
  readonly figureName?: string;
  readonly house?: number | null;
  readonly houseName?: string;

  readonly claims?: readonly RawClaim[];
}

/** One seat row under a card. `position` is `PositionKind`; `positionName` its title. */
export interface RawClaim {
  readonly parent?: number | null;
  readonly parentName?: string;
  readonly settlement?: number | null;
  readonly settlementName?: string;
  readonly position?: number | null;
  readonly positionName?: string;
  readonly cites?: number | readonly number[];
}

/**
 * Whether this session offers to be born: `?born=0` says it does not.
 *
 * **Default on, and gated on the URL alone** — never on `import.meta.env.DEV`,
 * which is O7's rule and the trap it names: the owner playtests the *deployed*
 * preview, so a start flow that only existed in development would be a start flow
 * nobody ever judged.
 *
 * A session that declines is the Phase 5 first minute in every way but one —
 * nobody is born, and the deed moment comes up a minute in with the name the
 * start menus took, since the owner ruled on 2026-08-24 that the typing belongs
 * there on every path (plan decision 4, *"two doors"*). So what this parameter
 * isolates is the *life* rather than the name, which is the comparison it exists
 * for.
 *
 * It lives here rather than in `session/params.ts` for the reason `?book` lives
 * in `witness/saga.ts`: a parameter belongs to whatever it turns on.
 */
export function bornRequested(search: string): boolean {
  const raw = new URLSearchParams(search).get(BORN_PARAM);

  if (raw === null) {
    return true;
  }

  const value = raw.trim().toLowerCase();

  return value !== "0" && value !== "false" && value !== "off";
}

/**
 * Whether this build can record a birth at all.
 *
 * Asked before the screen is put up, and it is a rule rather than a nicety: a
 * client that offered a life and then queued nothing would be a client that lied
 * about the one thing the screen is for. A build with the read and no door starts
 * the Phase 5 session instead, silently, which is the right answer to *"this
 * boundary is halfway through a wave"*.
 */
export function birthDoorOpen(door: BirthDoor): boolean {
  return (
    typeof door.queueBornOrphan === "function" ||
    typeof door.queueBornUnrecorded === "function" ||
    typeof door.queueBornBound === "function" ||
    typeof door.queueBornHeir === "function"
  );
}

/**
 * Reads every life the world will offer, refusing any card the log cannot
 * account for.
 *
 * The one call across the origins seam. The export is **presence-checked**, so a
 * build whose boundary has not grown it starts a session rather than throwing;
 * and the citation rule is enforced on the way in, because a life offered with no
 * record behind it is the same failure an uncited saga line is (decision O6's
 * rule, applied to the other projection this phase ships).
 *
 * Names are resolved here, from the source's own two answers: whatever the row
 * carried, and `settlements()` for a town the row named only by number. Nothing
 * else is consulted and nothing is invented — see the header.
 */
export function readOrigins(source: OriginsReading): OriginsOffer {
  const asked = source.origins;

  if (typeof asked !== "function") {
    return EMPTY_ORIGINS;
  }

  const towns = new Map<SettlementId, string>();

  for (const place of source.settlements()) {
    towns.set(place.settlement, place.name);
  }

  const cards: OriginChoice[] = [];
  const refused = { cards: 0, claims: 0 };

  for (const row of asked.call(source)) {
    const card = cardOf(row, towns, refused);

    if (card === null) {
      refused.cards += 1;

      continue;
    }

    cards.push(card);
  }

  return { cards, refused };
}

/**
 * Queues the birth a card names, and answers whether a door took it.
 *
 * **The client's whole involvement in being born.** Like every other deed, it is
 * a command rather than an edit: queued, applied inside a tick, written into the
 * replay — and refused silently by the simulation when the life it names is not
 * one the rules allow (`history::origins::resolve`). So the honest test that a
 * birth landed is to read `playerFigure()` afterwards, which is what the claim
 * machinery already does for every session (`witness/witness.ts`'s
 * `watchForClaim`), and what makes a born player a *claimed* player to everything
 * downstream of it.
 *
 * The name crosses **as typed**, for the reason `WorldSource.claim` states: the
 * replay format has to agree with exactly one normalization, and two that
 * disagree are a world that cannot be replayed.
 *
 * `false` means this build has no door for that card — not that the simulation
 * said no. Nothing can tell the caller *that* except the world afterwards.
 */
export function queueBirth(
  door: BirthDoor,
  choice: OriginChoice,
  name: string,
  /**
   * Which draw of a coined family name the player kept.
   *
   * {@link FIRST_COIN} is what pressing nothing does, and it is the default so
   * that every caller that does not offer the button reads as one that did not
   * press it (`witness/origins.ts`'s constant).
   */
  coinNonce: number = FIRST_COIN,
): boolean {
  if (choice.kind === "orphan") {
    const call = door.queueBornOrphan;

    if (typeof call !== "function" || choice.settlement === null) {
      return false;
    }

    call.call(door, name, choice.settlement, coinNonce);

    return true;
  }

  if (choice.kind === "unrecorded") {
    const call = door.queueBornUnrecorded;

    if (typeof call !== "function" || choice.mother === null || choice.father === null) {
      return false;
    }

    call.call(door, name, choice.mother, choice.father, coinNonce);

    return true;
  }

  if (choice.kind === "heir") {
    const call = door.queueBornHeir;

    if (typeof call !== "function" || choice.figure === null) {
      return false;
    }

    call.call(door, name, choice.figure, coinNonce);

    return true;
  }

  const call = door.queueBornBound;

  if (typeof call !== "function" || choice.figure === null) {
    return false;
  }

  call.call(door, name, choice.figure, coinNonce);

  return true;
}

/**
 * What a birth would coin as a family name at one press, or the empty string.
 *
 * **The button's whole content, and the rule it reads off the simulation.** The
 * empty string means *this life inherits a line and no press can rename it*
 * (the surname ruling's other half) — so a screen that shows the button only
 * where this answers with something is obeying the ruling rather than
 * transcribing it. It also means *this build has no preview*, and the two are
 * the same answer on purpose: neither is a button worth drawing.
 *
 * The name goes through as typed, like everything else that crosses: the wall
 * normalizes it, and a preview asked about a different string than the command
 * carries would be a preview about a different birth.
 */
export function coinedNameOf(
  door: BirthDoor,
  choice: OriginChoice,
  name: string,
  coinNonce: number,
): string {
  if (choice.kind === "orphan") {
    const ask = door.coinedOrphanHouse;

    return typeof ask === "function" && choice.settlement !== null
      ? ask.call(door, name, choice.settlement, coinNonce)
      : "";
  }

  if (choice.kind === "unrecorded") {
    const ask = door.coinedUnrecordedHouse;

    return typeof ask === "function" && choice.mother !== null && choice.father !== null
      ? ask.call(door, name, choice.mother, choice.father, coinNonce)
      : "";
  }

  // **The heir shares the bound door's preview**, and it is the same answer for
  // the same reason: both take over somebody the world already wrote down, so
  // both inherit whatever line that person carries and no press can rename it.
  // A life whose kin had no house is the one case either of them coins for.
  const ask = door.coinedBoundHouse;

  return typeof ask === "function" && choice.figure !== null
    ? ask.call(door, name, choice.figure, coinNonce)
    : "";
}

/**
 * Turns one row into a card, or `null` when there is no honest card in it.
 *
 * Three refusals, and each is a fact the door needs rather than a matter of
 * taste: a door this build does not know, a card that cites no record, and a card
 * that carries no way of being handed back to its own door (an orphan with no
 * town, an unrecorded child missing a parent, a bound child with no figure). The
 * last one is what stops a screen offering a life it cannot bind.
 */
function cardOf(
  row: RawOrigin,
  towns: ReadonlyMap<SettlementId, string>,
  refused: { cards: number; claims: number },
): OriginChoice | null {
  // The id first and the word behind it, so a boundary that ships only one of
  // them is read either way. `??` rather than `||`, because orphan is id zero.
  const kind = kindOf(row.kind ?? row.kindName);

  if (kind === null) {
    return null;
  }

  const cites = citesOf(row.cites);

  if (cites.length === 0) {
    return null;
  }

  const settlement = handleOf(row.settlement);
  const mother = handleOf(row.mother);
  const father = handleOf(row.father);
  const figure = handleOf(row.figure);

  if (kind === "orphan" && settlement === null) {
    return null;
  }

  if (kind === "unrecorded" && (mother === null || father === null)) {
    return null;
  }

  if ((kind === "bound" || kind === "heir") && figure === null) {
    return null;
  }

  const town = nameOf(row.settlementName, settlement, towns);
  const claims: OriginClaim[] = [];

  for (const claim of row.claims ?? []) {
    const said = claimOf(claim, towns);

    if (said === null) {
      refused.claims += 1;

      continue;
    }

    claims.push(said);
  }

  return {
    kind,
    line: lineOf(kind, {
      town,
      mother: row.motherName ?? "",
      father: row.fatherName ?? "",
      figure: row.figureName ?? "",
      house: row.houseName ?? "",
    }),
    claims,
    cites,
    settlement,
    mother,
    father,
    figure,
  };
}

/** The names a card's line is assembled from. Every one of them the source's own. */
interface CardNames {
  readonly town: string;
  readonly mother: string;
  readonly father: string;
  readonly figure: string;
  readonly house: string;
}

/**
 * The card's line: the source's nouns, with the plainest connectives between
 * them.
 *
 * The three fictions are the plan's own (decision 3) and the fantasy's before
 * that — *"you are an orphan in the town of X after it was sacked by Y"*, *"a
 * child no chronicle recorded"*, *"you were born into a noble family, rulers of
 * X"*. What varies below is only which clauses the source gave something to fill:
 * **a clause with no name behind it is dropped rather than filled**, which is the
 * whole of this file's honesty rule at the sentence level.
 */
function lineOf(kind: OriginKindName, names: CardNames): string {
  const of = names.town === "" ? "" : ` of ${names.town}`;

  if (kind === "orphan") {
    return names.town === ""
      ? "an orphan of a town an army came through"
      : `an orphan of ruined ${names.town}`;
  }

  const parents = parentsOf(names.mother, names.father);

  if (kind === "unrecorded") {
    if (parents === "") {
      return `a child no chronicle recorded${of}`;
    }

    return `a child no chronicle recorded — ${parents}'s`;
  }

  // Bound and heir: the life the world already wrote down, so the name on the
  // card is a name the log has been carrying for years rather than one the
  // screen made up.
  const who = names.figure === "" ? "a child the chronicles wrote down" : names.figure;
  const house = names.house === "" ? of : `, of house ${names.house}`;
  // **The one word the heir door adds**, and it is a fact rather than a
  // flourish: the card is on the screen because the line it belongs to is the
  // one that just ended, and a card that did not say so would be indistinguishable
  // from the bound one beside it.
  const line = kind === "heir" ? "of your line — " : "";

  if (parents === "") {
    return `${line}${who}${house}`;
  }

  return `${line}${who}, ${parents}'s child${house}`;
}

/** *"Solgerd and Bjorvald"*, *"Solgerd"*, or nothing at all. */
function parentsOf(mother: string, father: string): string {
  if (mother !== "" && father !== "") {
    return `${mother} and ${father}`;
  }

  return mother === "" ? father : mother;
}

/**
 * One seat a parent held, said plainly, or `null` when there is nothing honest to
 * say about it.
 *
 * A claim is the one thing on a noble card that is a *derivation* rather than a
 * fact off a record — the parent link plus the record that seated them
 * (`fantasy-01-born-into-the-world.md` §2: claims are pure projections) — so it
 * is held to the same rule as everything else on the screen: it cites the record
 * that made it true, and a row with neither an office nor a town has no sentence
 * this file is willing to invent for it.
 */
function claimOf(row: RawClaim, towns: ReadonlyMap<SettlementId, string>): OriginClaim | null {
  const cites = citesOf(row.cites);

  if (cites.length === 0) {
    return null;
  }

  const town = nameOf(row.settlementName, handleOf(row.settlement), towns);
  const seat = row.positionName ?? seatTitle(row.position);

  if (seat !== "" && town !== "") {
    return { line: `child of the ${seat} of ${town}`, cites };
  }

  if (seat !== "") {
    return { line: `child of a ${seat}`, cites };
  }

  if (town !== "") {
    return { line: `child of one who held a seat in ${town}`, cites };
  }

  return null;
}

/**
 * What the client calls a seat: ruler, hero, or nothing it is sure of.
 *
 * **The two words are transcribed from `PositionKind::title`**
 * (`crates/sim-core/src/history/event.rs` — ruler `0`, hero `1`), because the
 * boundary ships no name for a seat on purpose: an id is what a client groups by,
 * and the sentence a *record* carries is the simulation's own
 * (`world/types.ts`'s `OriginClaim.position`). The claim line the origins screen
 * draws is neither of those — it is a label on a menu — so it needs a word, and
 * this is the same trade `hud/panels.ts`'s `surfaceName` makes for the four beat
 * surfaces.
 *
 * **Transcribed means it can drift**, and the fallback is what makes that safe
 * rather than silent: an id this table does not know produces no word, the claim
 * falls back to the sentence that needs none ({@link claimOf}), and the record
 * behind it is in the trace view either way. A third seat minted sim-side turns
 * up as a plainer line and never as a wrong one. Preferring a `positionName` the
 * row carried, if a later boundary ever carries one, is the other half: the
 * moment the simulation says the word, this table stops being consulted.
 */
function seatTitle(position: number | null | undefined): string {
  return ["ruler", "hero"][position ?? -1] ?? "";
}

/**
 * Which door a row names, by id or by name, or `null` for one this build does not
 * know.
 *
 * Both spellings are read because the boundary may pass either — the sim's
 * `OriginKind` has stable ids *and* a `name()` a debug view labels with — and
 * because an id the client does not recognise is a door a later phase added,
 * which is a card to leave out rather than a reason to throw. Append-only ids are
 * the display-enum rule this project holds every one of them to.
 */
function kindOf(kind: number | string | undefined): OriginKindName | null {
  if (kind === 0 || kind === "orphan") {
    return "orphan";
  }

  if (kind === 1 || kind === "unrecorded") {
    return "unrecorded";
  }

  if (kind === 2 || kind === "bound") {
    return "bound";
  }

  if (kind === 3 || kind === "heir") {
    return "heir";
  }

  return null;
}

/** The records a row cites, as a list, whether it carried one id or several. */
function citesOf(cites: number | readonly number[] | undefined): readonly EventId[] {
  if (typeof cites === "number") {
    return Number.isFinite(cites) ? [cites] : [];
  }

  if (cites === undefined) {
    return [];
  }

  return cites.filter((event) => Number.isFinite(event));
}

/** A handle, or `null` — which is what a door that has none for this card answers. */
function handleOf(handle: number | null | undefined): number | null {
  return typeof handle === "number" && Number.isFinite(handle) ? handle : null;
}

/**
 * A town's name: whatever the row carried, or the one `settlements()` answers
 * with.
 *
 * Two sources for one string, and the row wins — a boundary that spelled the name
 * beside the number knows something this lookup does not, which for a ruined town
 * is exactly the case worth having. The lookup is the fallback because
 * `settlements()` is a read every session already makes.
 */
function nameOf(
  said: string | undefined,
  handle: SettlementId | null,
  towns: ReadonlyMap<SettlementId, string>,
): string {
  if (said !== undefined && said !== "") {
    return said;
  }

  if (handle === null) {
    return "";
  }

  return towns.get(handle) ?? "";
}
